import { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
    collection,
    query,
    onSnapshot,
    orderBy,
    where,
} from 'firebase/firestore';

interface UseFirebaseMenuResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

/**
 * Custom hook to fetch menu data in real-time from Firebase Firestore
 * Menus are stored as a subcollection: /Restaurants/{restaurantId}/menus/{menuId}
 * @param restaurantId - The restaurant ID to filter menus
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseMenuList = (restaurantId: string): UseFirebaseMenuResult<{ menus: any[] }> => {
    const [data, setData] = useState<{ menus: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for menu list as subcollection of restaurant
        const menusRef = collection(db, 'Restaurants', restaurantId, 'menus');
        const q = query(
            menusRef,       
            // where('supportedApps', 'array-contains', 'CustomerWeb'),
            where('status', '==', 'Active'),
            orderBy('displayOrder', 'asc'),
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const menus = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setData({ menus });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching menus:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [restaurantId]);

    return { data, loading, error };
};

/**
 * Custom hook to fetch setMenus data in real-time from Firebase Firestore
 * SetMenus are stored as a subcollection: /Restaurants/{restaurantId}/setMenus/{setMenuId}
 * @param restaurantId - The restaurant ID to filter setMenus
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseSetMenuList = (restaurantId: string): UseFirebaseMenuResult<{ setMenus: any[] }> => {
    const [data, setData] = useState<{ setMenus: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for setMenus list as subcollection of restaurant
        const setMenusRef = collection(db, 'Restaurants', restaurantId, 'setMenus');
        const q = query(
            setMenusRef,
            where('status', '==', 'Active')
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const setMenus = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setData({ setMenus });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching setMenus:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [restaurantId]);

    return { data, loading, error };
};

/**
 * Custom hook to fetch customMenus data in real-time from Firebase Firestore
 * CustomMenus are stored as a subcollection: /Restaurants/{restaurantId}/customMenus/{customMenuId}
 * @param restaurantId - The restaurant ID to filter customMenus
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseCustomMenuList = (restaurantId: string): UseFirebaseMenuResult<{ customMenus: any[] }> => {
    const [data, setData] = useState<{ customMenus: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for customMenus list as subcollection of restaurant
        const customMenusRef = collection(db, 'Restaurants', restaurantId, 'customMenus');
        const q = query(
            customMenusRef,
            where('status', '==', 'Active'),
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const customMenus = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setData({ customMenus });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching customMenus:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [restaurantId]);

    return { data, loading, error };
};

/**
 * Custom hook to fetch menuItems data in real-time from Firebase Firestore
 * MenuItems are stored as a subcollection: /Restaurants/{restaurantId}/menuItems/{menuItemId}
 * @param restaurantId - The restaurant ID to filter menuItems
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseMenuItemsList = (restaurantId: string): UseFirebaseMenuResult<{ menuItems: any[] }> => {
    const [data, setData] = useState<{ menuItems: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for menuItems list as subcollection of restaurant
        const menuItemsRef = collection(db, 'Restaurants', restaurantId, 'menuItems');
        const q = query(
            menuItemsRef,
            where('status', '==', 'Active')
        );

        // Set up real-time listener
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const menuItems = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setData({ menuItems });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching menuItems:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [restaurantId]);
    return { data, loading, error };
};

/**
 * Custom hook to fetch customMenuItemTypes data in real-time from Firebase Firestore
 * CustomMenuItemTypes are stored as a subcollection: /Restaurants/{restaurantId}/customMenuItemTypes/{typeId}
 * Also calculates totalCount and availableCount based on menuItems that reference each type
 * @param restaurantId - The restaurant ID to filter customMenuItemTypes
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseCustomMenuItemTypes = (restaurantId: string): UseFirebaseMenuResult<{ customMenuItemTypes: any[] }> => {
    const [data, setData] = useState<{ customMenuItemTypes: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for customMenuItemTypes list as subcollection of restaurant
        const customMenuItemTypesRef = collection(db, 'Restaurants', restaurantId, 'customMenuItemTypes');
        const menuItemsRef = collection(db, 'Restaurants', restaurantId, 'menuItems');
        
        const typesQuery = query(customMenuItemTypesRef);
        const itemsQuery = query(menuItemsRef, where('status', '==', 'Active'));

        // Set up real-time listeners for both collections
        let typesData: any[] = [];
        let itemsData: any[] = [];
        let typesLoaded = false;
        let itemsLoaded = false;

        const calculateCounts = () => {
            if (!typesLoaded || !itemsLoaded) return;

            // Calculate counts for each type
            const enrichedTypes = typesData.map(type => {
                const relatedItems = itemsData.filter(item => item.customMenuItemTypeId === type.id);
                const totalCount = relatedItems.length;
                const availableCount = relatedItems.filter(item => item.isAvailable === true).length;

                return {
                    ...type,
                    totalCount,
                    availableCount
                };
            });

            setData({ customMenuItemTypes: enrichedTypes });
            setLoading(false);
            setError(null);
        };

        const unsubscribeTypes = onSnapshot(
            typesQuery,
            (snapshot) => {
                typesData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                typesLoaded = true;
                calculateCounts();
            },
            (err) => {
                console.error('Error fetching customMenuItemTypes:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        const unsubscribeItems = onSnapshot(
            itemsQuery,
            (snapshot) => {
                itemsData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                itemsLoaded = true;
                calculateCounts();
            },
            (err) => {
                console.error('Error fetching menuItems for count calculation:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listeners on unmount
        return () => {
            unsubscribeTypes();
            unsubscribeItems();
        };
    }, [restaurantId]);

    return { data, loading, error };
};

/**
 * Custom hook to fetch holidays data in real-time from Firebase Firestore
 * Holidays are stored as a subcollection: /Restaurants/{restaurantId}/holiday/{holidayId}
 * @param restaurantId - The restaurant ID to filter holidays
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseHolidayList = (restaurantId: string): UseFirebaseMenuResult<{ holidays: any[] }> => {
    const [data, setData] = useState<{ holidays: any[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!restaurantId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Query for holidays list as subcollection of restaurant
        const holidaysRef = collection(db, 'Restaurants', restaurantId, 'holidays');
        
        // Set up real-time listener (no filters)
        const unsubscribe = onSnapshot(
            holidaysRef,
            (snapshot) => {
                const holidays = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setData({ holidays });
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching holidays:', err);
                setError(err as Error);
                setLoading(false);
            }
        );

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [restaurantId]);

    return { data, loading, error };
};